import { useQuery } from '@tanstack/react-query';
import { useOkapiKy } from '@folio/stripes/core';

const PER_PAGE = 30;

/**
 * Build CQL query from URL search params for scheduled reports.
 */
export function buildScheduledReportsCQL(searchParams) {
  const parts = [];

  const q = searchParams.get('query')?.trim();
  if (q) parts.push(`name=="${q}*"`);

  const filters = searchParams.get('filters') || '';
  if (filters) {
    const filterMap = {};
    filters.split(',').forEach(f => {
      const dot = f.indexOf('.');
      if (dot === -1) return;
      const name = f.substring(0, dot);
      const value = f.substring(dot + 1);
      if (!filterMap[name]) filterMap[name] = [];
      filterMap[name].push(value);
    });

    const cqlMap = {
      isActive:     'isActive',
      frequency:    'frequency',
      outputFormat: 'outputFormat',
    };

    Object.entries(filterMap).forEach(([name, values]) => {
      const field = cqlMap[name];
      if (!field || !values.length) return;
      const or = values.map(v => `${field}=="${v}"`).join(' or ');
      parts.push(values.length > 1 ? `(${or})` : or);
    });
  }

  const sort = searchParams.get('sort') || 'name';
  const sortDir = sort.startsWith('-') ? '/sort.descending' : '/sort.ascending';
  const cql = parts.length ? parts.join(' and ') : 'cql.allRecords=1';
  return `${cql} sortby ${sort.replace(/^-/, '')}${sortDir}`;
}

export default function useScheduledReportsListAPI({ searchParams, offset = 0 }) {
  const okapiKy = useOkapiKy();
  const cql = buildScheduledReportsCQL(searchParams);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['scheduled-reports-list', cql, offset],
    queryFn: () =>
      okapiKy
        .get(`scheduled-reports?limit=${PER_PAGE}&offset=${offset}&query=${encodeURIComponent(cql)}`)
        .json(),
    staleTime: 2 * 60 * 1000,
    keepPreviousData: true,
    retry: 1,
  });

  return {
    scheduledReports: data?.scheduledReports || [],
    totalRecords:     data?.totalRecords || 0,
    isLoading,
    isFetching,
    refetch,
    cql,
  };
}
